import { Link } from "react-router-dom";
import { ArrowLeft, Check, FileText, Info, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";

const LOOKING_FOR = ["Designs", "Shade numbers", "GSM, width & composition", "Swatch images"];
// Swatch colours on the illustrated page.
const SWATCHES = ["#C9B79C", "#8FA9A3", "#D8A47F", "#6E7F99", "#B9867A", "#E2D5BF"];

// The extractor reports `done/total` over batches of pages (catalogue_extract.py). When it reaches
// total, the server is still cropping and uploading swatch images, so that's its own stage.
function phaseOf(record) {
  const { done = 0, total = 0 } = record.progress || {};
  if (record.status === "QUEUED") return { stage: 0, percent: 3, caption: "Waiting to start…" };
  if (!total) return { stage: 1, percent: 5, caption: "Opening the PDF…", indeterminate: true };
  if (done < total) {
    return { stage: 1, percent: Math.round(5 + (done / total) * 85), caption: `Reading pages · batch ${done + 1} of ${total}` };
  }
  return { stage: 2, percent: 95, caption: "Cropping swatches and saving images…" };
}

function CatalogueIllustration({ percent }) {
  return (
    <div className="relative mx-auto h-44 w-36" aria-hidden="true">
      {/* pages underneath */}
      <span className="absolute inset-0 translate-x-3 translate-y-2 rotate-6 rounded-lg bg-card border border-border shadow-sm" />
      <span className="absolute inset-0 translate-x-1.5 translate-y-1 rotate-3 rounded-lg bg-card border border-border shadow-sm" />
      {/* top page */}
      <span className="absolute inset-0 rounded-lg bg-card border border-border shadow-md overflow-hidden p-3">
        <span className="block h-1.5 w-16 rounded bg-foreground/70" />
        <span className="block mt-1.5 h-1 w-10 rounded bg-muted-foreground/40" />
        <span className="mt-3 grid grid-cols-3 gap-1.5">
          {SWATCHES.map((c) => (
            <span key={c} className="aspect-square rounded-[3px]" style={{ background: c }} />
          ))}
        </span>
        <span className="block mt-3 h-1 w-full rounded bg-muted-foreground/25" />
        <span className="block mt-1 h-1 w-4/5 rounded bg-muted-foreground/25" />
        <span className="block mt-1 h-1 w-3/5 rounded bg-muted-foreground/25" />
        {/* scanning beam */}
        <span className="hi-scan absolute inset-x-0 h-10 bg-gradient-to-b from-transparent via-accent/25 to-transparent">
          <span className="absolute inset-x-0 top-1/2 h-px bg-accent/70" />
        </span>
      </span>
      <span className="absolute -bottom-3 -right-4 rounded-full bg-accent text-accent-foreground font-serif text-sm px-2.5 py-1 shadow-md tabular-nums">
        {percent}%
      </span>
    </div>
  );
}

function Step({ state, title, detail }) {
  return (
    <li className="flex items-start gap-3">
      <span
        className={cn(
          "mt-0.5 h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-colors",
          state === "done" && "bg-primary text-primary-foreground",
          state === "active" && "bg-accent/10 text-accent ring-1 ring-accent/40",
          state === "todo" && "bg-card border border-border text-muted-foreground",
        )}
      >
        {state === "done" ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : state === "active" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      </span>
      <span className="min-w-0">
        <span className={cn("block text-sm", state === "todo" ? "text-muted-foreground" : "text-foreground font-medium")}>{title}</span>
        {detail && <span className="block text-xs text-muted-foreground mt-0.5">{detail}</span>}
      </span>
    </li>
  );
}

export default function ImportProgress({ record }) {
  const phase = phaseOf(record);
  const { done = 0, total = 0 } = record.progress || {};
  const stateFor = (i) => (i < phase.stage ? "done" : i === phase.stage ? "active" : "todo");

  return (
    <section className="max-w-4xl bg-card border border-border rounded-2xl overflow-hidden hi-animate-fade-up" data-testid="import-progress" aria-live="polite">
      <div className="grid md:grid-cols-[16rem_minmax(0,1fr)]">
        <div className="bg-secondary/50 border-b md:border-b-0 md:border-r border-border px-6 py-9 flex flex-col items-center justify-center text-center">
          <CatalogueIllustration percent={phase.percent} />
          <p className="mt-8 text-sm text-foreground/80" data-testid="import-progress-caption">{phase.caption}</p>
          {record.createdAt && <p className="text-xs text-muted-foreground mt-1">Started {timeAgo(record.createdAt)}</p>}
        </div>

        <div className="p-6 sm:p-8">
          <h2 className="font-serif text-2xl text-foreground">Reading your catalogue</h2>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-lg">
            We go through every page to pull out each design and its shades, so you only have to review and publish. Large catalogues can take several minutes.
          </p>

          <ol className="mt-6 space-y-4">
            <Step state={stateFor(0)} title="PDF uploaded" detail={stateFor(0) === "active" ? "Queued — starts in a moment" : null} />
            <Step
              state={stateFor(1)}
              title="Finding designs, shades and specs"
              detail={stateFor(1) === "active" && total ? `${done} of ${total} page batches read` : null}
            />
            <Step state={stateFor(2)} title="Cropping swatch images" />
            <Step state="todo" title="Ready for your review" detail="Check names and prices, untick anything you don't stock, then publish." />
          </ol>

          <div className="mt-7">
            <div className="h-2 rounded-full bg-secondary overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={phase.percent}>
              {phase.indeterminate ? (
                <div className="h-full w-full hi-shimmer" />
              ) : (
                <div className="h-full rounded-full bg-gradient-to-r from-primary to-primary/80 transition-[width] duration-700 ease-out" style={{ width: `${phase.percent}%` }} />
              )}
            </div>
            <div className="mt-2 flex justify-between text-xs text-muted-foreground tabular-nums">
              <span>{total > 0 ? `Step ${Math.min(done, total)} of ${total}` : "Preparing…"}</span>
              <span>{phase.percent}%</span>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <span className="text-xs text-muted-foreground mr-1 self-center">Looking for</span>
            {LOOKING_FOR.map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-secondary/80 px-3 py-1 text-xs text-foreground/80">
                <FileText className="h-3 w-3 text-muted-foreground" /> {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 border-t border-border bg-secondary/30 px-6 py-4">
        <p className="flex items-start gap-2 text-sm text-muted-foreground flex-1">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          You can leave this page — the import keeps running. Find it any time under Catalogues.
        </p>
        <Button variant="outline" asChild className="bg-card shrink-0">
          <Link to="/catalogues">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Catalogues
          </Link>
        </Button>
      </div>
    </section>
  );
}
